import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FaTimes, FaSave, FaReceipt, FaSync } from 'react-icons/fa';
import toast from 'react-hot-toast';
import './GastoFormModal.css';
import { CREAR_GASTO, ACTUALIZAR_GASTO, GET_GASTOS } from '../../graphql/gastos';
import {
  GET_CATEGORIAS_GASTO,
  GET_PROVEEDORES
} from '../../graphql/proveedores';
import FileUpload from '../shared/FileUpload';

const today = () => new Date().toISOString().slice(0, 10);

const INITIAL_FORM = {
  categoria_gasto_id: '',
  proveedor_id: '',
  fecha: today(),
  descripcion: '',
  monto: '',
  iva_porcentaje: '0',
  metodo_pago: 'efectivo',
  referencia_pago: '',
  numero_factura_proveedor: '',
  recurrente: false,
  frecuencia_recurrencia: 'mensual',
  notas: '',
  comprobante_url: null
};

// Usage:
// <GastoFormModal isOpen={open} onClose={() => setOpen(false)} gasto={null} />
// <GastoFormModal isOpen={open} onClose={() => setOpen(false)} gasto={gastoObj} />

const GastoFormModal = ({ isOpen, onClose, gasto = null }) => {
  const isEditing = !!gasto;
  const [formData, setFormData] = useState(INITIAL_FORM);

  const { data: dataCategoriasGasto } = useQuery(GET_CATEGORIAS_GASTO, {
    variables: { activa: true },
    skip: !isOpen
  });

  const { data: dataProveedores } = useQuery(GET_PROVEEDORES, {
    variables: { activo: true },
    skip: !isOpen
  });

  const [crearGasto, { loading: creando }] = useMutation(CREAR_GASTO, {
    refetchQueries: [{ query: GET_GASTOS }]
  });

  const [actualizarGasto, { loading: actualizando }] = useMutation(ACTUALIZAR_GASTO, {
    refetchQueries: [{ query: GET_GASTOS }]
  });

  useEffect(() => {
    if (gasto) {
      setFormData({
        categoria_gasto_id: gasto.categoria_gasto_id?.toString() || '',
        proveedor_id: gasto.proveedor_id?.toString() || '',
        fecha: gasto.fecha ? gasto.fecha.slice(0, 10) : today(),
        descripcion: gasto.descripcion || '',
        monto: gasto.monto?.toString() || '',
        iva_porcentaje: gasto.iva_porcentaje?.toString() || '0',
        metodo_pago: gasto.metodo_pago || 'efectivo',
        referencia_pago: gasto.referencia_pago || '',
        numero_factura_proveedor: gasto.numero_factura_proveedor || '',
        recurrente: gasto.recurrente || false,
        frecuencia_recurrencia: gasto.frecuencia_recurrencia || 'mensual',
        notas: gasto.notas || '',
        comprobante_url: gasto.comprobante_url || null
      });
    } else {
      setFormData(INITIAL_FORM);
    }
  }, [gasto, isOpen]);

  const categorias = dataCategoriasGasto?.categoriasGasto || [];
  const proveedores = dataProveedores?.proveedores || [];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { ivaMonto, total } = useMemo(() => {
    const monto = parseFloat(formData.monto) || 0;
    const ivaPct = parseFloat(formData.iva_porcentaje) || 0;
    const iva = monto * (ivaPct / 100);
    return { ivaMonto: iva, total: monto + iva };
  }, [formData.monto, formData.iva_porcentaje]);

  const fmt = (n) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(n);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.categoria_gasto_id) {
      toast.error('Debe seleccionar una categoria de gasto');
      return;
    }
    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      toast.error('El monto debe ser mayor a cero');
      return;
    }
    if (!formData.descripcion || !formData.descripcion.trim()) {
      toast.error('La descripcion es requerida');
      return;
    }

    try {
      const input = {
        categoria_gasto_id: parseInt(formData.categoria_gasto_id),
        proveedor_id: formData.proveedor_id ? parseInt(formData.proveedor_id) : null,
        fecha: formData.fecha,
        descripcion: formData.descripcion.trim(),
        monto: parseFloat(formData.monto),
        iva_porcentaje: parseFloat(formData.iva_porcentaje) || 0,
        metodo_pago: formData.metodo_pago,
        referencia_pago: formData.referencia_pago.trim() || null,
        numero_factura_proveedor: formData.numero_factura_proveedor.trim() || null,
        recurrente: formData.recurrente,
        frecuencia_recurrencia: formData.recurrente ? formData.frecuencia_recurrencia : null,
        notas: formData.notas.trim() || null,
        comprobante_url: formData.comprobante_url || null
      };

      if (isEditing) {
        await actualizarGasto({ variables: { id: parseInt(gasto.id), input } });
        toast.success('Gasto actualizado correctamente');
      } else {
        await crearGasto({ variables: { input } });
        toast.success('Gasto registrado correctamente');
      }

      onClose();
    } catch (error) {
      console.error('Error al guardar gasto:', error);
      toast.error(error.message || 'Error al guardar el gasto');
    }
  };

  if (!isOpen) return null;

  const loading = creando || actualizando;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="gasto-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gasto-form-header">
          <h2>
            <FaReceipt />
            {isEditing ? 'Editar Gasto' : 'Registrar Gasto'}
          </h2>
          <button className="close-button" onClick={onClose} type="button" aria-label="Cerrar">
            <FaTimes />
          </button>
        </div>

        <form className="gasto-form" onSubmit={handleSubmit} noValidate>
          {/* Clasificacion */}
          <div className="form-section">
            <h3>Clasificacion</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="gf-categoria">Categoria *</label>
                <select
                  id="gf-categoria"
                  value={formData.categoria_gasto_id}
                  onChange={(e) => handleChange('categoria_gasto_id', e.target.value)}
                  required
                >
                  <option value="">Seleccione una categoria</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="gf-proveedor">Proveedor (opcional)</label>
                <select
                  id="gf-proveedor"
                  value={formData.proveedor_id}
                  onChange={(e) => handleChange('proveedor_id', e.target.value)}
                >
                  <option value="">Sin proveedor</option>
                  {proveedores.map((prov) => (
                    <option key={prov.id} value={prov.id}>
                      {prov.nombre_comercial || prov.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="gf-fecha">Fecha *</label>
                <input
                  id="gf-fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => handleChange('fecha', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="gf-descripcion">Descripcion</label>
              <input
                id="gf-descripcion"
                type="text"
                value={formData.descripcion}
                onChange={(e) => handleChange('descripcion', e.target.value)}
                placeholder="Descripcion breve del gasto"
              />
            </div>
          </div>

          {/* Montos */}
          <div className="form-section">
            <h3>Montos</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="gf-monto">Monto Base *</label>
                <input
                  id="gf-monto"
                  type="number"
                  value={formData.monto}
                  onChange={(e) => handleChange('monto', e.target.value)}
                  required
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="gf-iva">IVA (%)</label>
                <select
                  id="gf-iva"
                  value={formData.iva_porcentaje}
                  onChange={(e) => handleChange('iva_porcentaje', e.target.value)}
                >
                  <option value="0">0% - Excluido</option>
                  <option value="5">5%</option>
                  <option value="19">19%</option>
                </select>
              </div>

              <div className="form-group">
                <label>IVA Calculado</label>
                <div className="monto-calculado monto-calculado--iva">{fmt(ivaMonto)}</div>
              </div>

              <div className="form-group">
                <label>Total</label>
                <div className="monto-calculado monto-calculado--total">{fmt(total)}</div>
              </div>
            </div>
          </div>

          {/* Pago */}
          <div className="form-section">
            <h3>Informacion de Pago</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="gf-metodo-pago">Metodo de Pago *</label>
                <select
                  id="gf-metodo-pago"
                  value={formData.metodo_pago}
                  onChange={(e) => handleChange('metodo_pago', e.target.value)}
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="cheque">Cheque</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="gf-referencia">Referencia de Pago</label>
                <input
                  id="gf-referencia"
                  type="text"
                  value={formData.referencia_pago}
                  onChange={(e) => handleChange('referencia_pago', e.target.value)}
                  placeholder="No. transferencia, comprobante..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="gf-factura">No. Factura Proveedor</label>
                <input
                  id="gf-factura"
                  type="text"
                  value={formData.numero_factura_proveedor}
                  onChange={(e) => handleChange('numero_factura_proveedor', e.target.value)}
                  placeholder="Ej: FAC-2024-001"
                />
              </div>
            </div>
          </div>

          {/* Recurrencia */}
          <div className="form-section">
            <h3>Recurrencia</h3>

            <div className="recurrente-toggle">
              <label className="toggle-label" htmlFor="gf-recurrente">
                <input
                  id="gf-recurrente"
                  type="checkbox"
                  checked={formData.recurrente}
                  onChange={(e) => handleChange('recurrente', e.target.checked)}
                />
                <span className="toggle-track" />
                <span className="toggle-text">
                  <FaSync />
                  Este es un gasto recurrente
                </span>
              </label>
            </div>

            {formData.recurrente && (
              <div className="form-group form-group--inline">
                <label htmlFor="gf-frecuencia">Frecuencia</label>
                <select
                  id="gf-frecuencia"
                  value={formData.frecuencia_recurrencia}
                  onChange={(e) => handleChange('frecuencia_recurrencia', e.target.value)}
                >
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                  <option value="bimestral">Bimestral</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="semestral">Semestral</option>
                  <option value="anual">Anual</option>
                </select>
              </div>
            )}
          </div>

          {/* Notas */}
          <div className="form-section">
            <h3>Notas</h3>
            <div className="form-group">
              <textarea
                value={formData.notas}
                onChange={(e) => handleChange('notas', e.target.value)}
                placeholder="Observaciones adicionales sobre este gasto..."
                rows="3"
              />
            </div>
          </div>

          {/* Comprobante */}
          <div className="form-section form-section--last">
            <h3>Comprobante / Factura</h3>
            <FileUpload
              value={formData.comprobante_url}
              onChange={(url) => handleChange('comprobante_url', url)}
              label="Adjuntar imagen o PDF del comprobante"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-guardar" disabled={loading}>
              <FaSave />
              {loading ? 'Guardando...' : isEditing ? 'Actualizar Gasto' : 'Registrar Gasto'}
            </button>
            <button type="button" className="btn-cancelar" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GastoFormModal;
